import { useState, useEffect, useRef } from "react";
import { supabase } from "./db";

// 👇 FIXNÉ STĹPCE PRE DODÁVATEĽA
const FIXED_COLUMNS = ["image_url", "status", "content_status", "internal_sku"];

// Mapovanie systémových názvov na slovenské (zhodné s Admin/Master Dashboardom)
const COLUMN_NAMES_MAP = {
  ean: "EAN Kód",
  product_name: "Názov produktu",
  category: "Kategória na webe",
  price: "Cena výrobcu (€)",
  stock: "Sklad (ks)",
  brand: "Brand",
  created_at: "Dátum pridania",
  approved_at: "Čas rozhodnutia",
  weight_brutto_g: "Váha Brutto (g)",
  weight_netto_g: "Váha Netto (g)",
  short_description: "Krátky text",
  description: "Hlavný popis",
  image_url: "Foto",
  product_line: "Produktová rada",
  sukl: "ŠÚKL",
  adc: "ADC kód",
  predecessor: "Predchodca",
  dr_max_code: "Kód Dr.Max",
  base_mj: "Základná MJ",
  ratio_to_zmj: "Pomer k ZMJ",
  packaging: "Balenie",
  ingredients: "Zloženie",
  dosage: "Dávkovanie",
  nutritional_values: "Výživové hodnoty",
  width_mm: "Šírka (mm)",
  height_mm: "Výška (mm)",
  depth_mm: "Hĺbka (mm)",
  pieces_in_carton: "Ks v kartóne",
  pieces_on_pallet: "Ks na palete",
  manufacturer: "Výrobca",
  country_of_origin: "Krajina pôvodu",
  customs_code: "Colný sadzobník",
  recycling_fee: "Recyklačný poplatok",
  vat_percent: "% DPH",
  dmoc: "DMOC (€)",
  returnable_packaging: "Zálohovaný obal",
  total_weight: "Celková hmotnosť",
  listing_date: "Zalistovanie od",
  listing_type: "Typ listingu",
  admin_fee: "Admin poplatok",
  turnover_reward_percent: "Odplata z obratu (%)",
  tracked_transaction: "Sledovaná transakcia",
  tracked_price: "Sledovaná cena"
};

export default function SupplierProductList({ session }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Stavy pre dynamické stĺpce
  const [dbColumns, setDbColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([
    "product_name", "ean", "category", "price", "stock", "created_at"
  ]);
  const [showColumnChooser, setShowColumnChooser] = useState(false);
  const columnChooserRef = useRef(null);

  // Filtre a Triedenie
  const [searchTerm, setSearchTerm] = useState("");
  const [colFilters, setColFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Stránkovanie
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  useEffect(() => {
    fetchColumnsFromDB();
    fetchMyProducts();
  }, []);

  // Zatváranie menu pri kliknutí mimo
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnChooserRef.current && !columnChooserRef.current.contains(event.target)) {
        setShowColumnChooser(false);
      }
    };
    if (showColumnChooser) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showColumnChooser]);

  const fetchColumnsFromDB = async () => {
    const { data, error } = await supabase.rpc("get_products_columns");
    if (!error && data && data.length > 0) {
      const exclude = ["id", "supplier_id", "locked_by_master", "updated_at", ...FIXED_COLUMNS];
      let cleanCols = data.filter(c => !exclude.includes(c));
      cleanCols.sort((a, b) => (COLUMN_NAMES_MAP[a] || a).localeCompare(COLUMN_NAMES_MAP[b] || b));
      setDbColumns(cleanCols);
    }
  };

  const fetchMyProducts = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .eq("supplier_id", session.user.id)
      .order("created_at", { ascending: false });

    if (data) setProducts(data);
    if (error) console.error("Chyba pri načítaní produktov:", error);
    setLoading(false);
  };

  // 👇 FILTRÁCIA
  const filteredProducts = products.filter((p) => {
    const searchLower = searchTerm.toLowerCase();
    
    const matchesGlobal = !searchTerm || [
      p.product_name, p.ean, p.internal_sku
    ].some(val => String(val || "").toLowerCase().includes(searchLower));

    if (!matchesGlobal) return false;

    const activeFilters = Object.entries(colFilters).filter(([, val]) => val !== "");
    
    for (const [key, filterVal] of activeFilters) {
      let cellVal = p[key];

      if(key === "status" && !cellVal) cellVal = "Čaká na schválenie";
      if(key === "content_status" && !cellVal) cellVal = "Spracúva sa";

      if (!String(cellVal || "").toLowerCase().includes(filterVal.toLowerCase())) {
        return false; 
      }
    }

    return true;
  });

  // 👇 ZORAĎOVANIE
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    let valA = a[sortConfig.key];
    let valB = b[sortConfig.key];

    if (sortConfig.key === "created_at" || sortConfig.key === "approved_at") {
      valA = new Date(valA || 0).getTime();
      valB = new Date(valB || 0).getTime();
    } else if (!isNaN(parseFloat(valA)) && !isNaN(parseFloat(valB))) {
      valA = parseFloat(valA) || 0;
      valB = parseFloat(valB) || 0;
    } else {
      valA = String(valA || "").toLowerCase();
      valB = String(valB || "").toLowerCase();
    }

    if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
    if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) return " ↕️";
    return sortConfig.direction === "asc" ? " 🔼" : " 🔽";
  };

  const updateColFilter = (field, value) => {
    setColFilters((prev) => ({ ...prev, [field]: value }));
  };

  // Stránkovanie
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = itemsPerPage === "all" ? sortedProducts : sortedProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = itemsPerPage === "all" ? 1 : Math.ceil(sortedProducts.length / itemsPerPage);
  
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  useEffect(() => { setCurrentPage(1); }, [colFilters, searchTerm]);

  return (
    <div style={{ width: "100%" }}>
      
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "10px" }}>
        <input 
          type="text" 
          placeholder="🔍 Hľadať (Názov, EAN, SKU)..." 
          value={searchTerm} 
          onChange={(e) => setSearchTerm(e.target.value)} 
          style={{ width: "100%", maxWidth: "400px", padding: "10px 16px", borderRadius: "10px", border: "1px solid #cbd5e1", outline: "none", fontSize: "14px" }} 
        />
        
        {/* COLUMN CHOOSER */}
        <div style={{ position: "relative" }} ref={columnChooserRef}>
          <button onClick={() => setShowColumnChooser(!showColumnChooser)} style={{ padding: "10px 16px", borderRadius: "10px", border: "1px solid #cbd5e1", backgroundColor: "white", cursor: "pointer", display: "flex", gap: "8px", alignItems: "center", fontWeight: "600", color: "#475569" }}>
            ⚙️ Zobraziť stĺpce ({selectedColumns.length + FIXED_COLUMNS.length})
          </button>
          
          {showColumnChooser && (
            <div style={columnChooserPopupStyle}>
              <div style={popupHeaderStyle}>Vyberte stĺpce pre výpis:</div>
              <div style={popupContentStyle}>
                {FIXED_COLUMNS.map(col => (
                  <label key={col} style={popupItemDisabledStyle}>
                    <input type="checkbox" checked disabled style={{ margin: 0, cursor: 'not-allowed', width: "16px", height: "16px", flexShrink: 0 }} />
                    <span style={{ flex: 1, textAlign: "left" }}>
                      {col === 'status' ? "Stav schválenia" : col === 'content_status' ? "Web Status" : col === 'image_url' ? "Foto" : "Interné SKU"}
                      <span style={{color: '#94a3b8', fontSize: '11px', fontStyle: 'italic', marginLeft: '5px'}}>(fixný)</span>
                    </span>
                  </label>
                ))}
                {dbColumns.map(col => (
                  <label key={col} style={popupItemStyle}>
                    <input 
                      type="checkbox" 
                      checked={selectedColumns.includes(col)}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedColumns([...selectedColumns, col]);
                        else setSelectedColumns(selectedColumns.filter(c => c !== col));
                      }}
                      style={{ margin: 0, cursor: "pointer", width: "16px", height: "16px", flexShrink: 0 }}
                    />
                    <span style={{ flex: 1, textAlign: "left" }}>{COLUMN_NAMES_MAP[col] || col}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: "12px", minHeight: "300px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", backgroundColor: "white" }}>
          <thead>
            <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
              
              {/* FIXNÉ STĹPCE */}
              <th style={{ ...thStyle, width: "60px", textAlign: "center" }}>Foto</th>
              
              <th style={{ ...thStyle, minWidth: "150px" }}>
                <div style={sortableHeaderContent} onClick={() => requestSort("status")}>Stav {getSortIcon("status")}</div>
                <select value={colFilters.status || ""} onChange={(e) => updateColFilter("status", e.target.value)} style={filterInputStyle}>
                  <option value="">Všetky stavy</option>
                  <option value="Čaká na schválenie">Čaká na schválenie</option>
                  <option value="Schválené">Schválené</option>
                  <option value="Neschválené">Neschválené</option>
                </select>
              </th>

              <th style={{ ...thStyle, backgroundColor: "#f0fdf4", minWidth: "160px" }}>
                <div style={sortableHeaderContent} onClick={() => requestSort("content_status")}>Web Status {getSortIcon("content_status")}</div>
                <select value={colFilters.content_status || ""} onChange={(e) => updateColFilter("content_status", e.target.value)} style={filterInputStyle}>
                  <option value="">Všetky</option>
                  <option value="Spracúva sa">Spracúva sa</option>
                  <option value="Zalistované na webe">Zalistované na webe</option>
                </select>
              </th>

              <th style={{ ...thStyle, backgroundColor: "#fffbeb", minWidth: "160px" }}>
                <div style={sortableHeaderContent} onClick={() => requestSort("internal_sku")}>Interné SKU {getSortIcon("internal_sku")}</div>
                <input type="text" style={filterInputStyle} placeholder="Filter SKU..." onChange={(e) => updateColFilter("internal_sku", e.target.value)} />
              </th>

              {/* DYNAMICKÉ STĹPCE */}
              {selectedColumns.map(colKey => {
                const label = COLUMN_NAMES_MAP[colKey] || colKey;
                const minWidth = colKey === "short_description" || colKey === "description" ? "300px" : "150px";

                return (
                  <th key={colKey} style={{ ...thStyle, minWidth }}>
                    <div style={sortableHeaderContent} onClick={() => requestSort(colKey)}>{label} {getSortIcon(colKey)}</div>
                    <input type="text" style={filterInputStyle} placeholder={`Filter...`} onChange={(e) => updateColFilter(colKey, e.target.value)} />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={selectedColumns.length + 4} style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>Načítavam históriu produktov...</td></tr>
            ) : currentItems.length === 0 ? (
              <tr><td colSpan={selectedColumns.length + 4} style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>Zadaným filtrom nezodpovedá žiadny produkt.</td></tr>
            ) : currentItems.map((p) => (
              <tr key={p.id} style={{ borderBottom: "1px solid #e2e8f0", background: p.status === "Neschválené" ? "#fef2f2" : "transparent" }}>
                
                {/* FIXNÉ HODNOTY */}
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  {p.image_url ? <img src={p.image_url} style={{ width: "40px", borderRadius: "4px", objectFit: "cover" }} alt="produkt" /> : <span style={{fontSize: "10px", color: "#94a3b8"}}>Bez foto</span>}
                </td>
                <td style={tdStyle}>
                  <span style={p.status === "Schválené" ? badgeSuccess : p.status === "Neschválené" ? badgeDanger : badgeWarning}>
                    {p.status || "Čaká na schválenie"}
                  </span>
                </td>
                <td style={{ ...tdStyle, backgroundColor: "#f0fdf4" }}>
                  {p.content_status ? (
                    <span style={{ display: "inline-block", whiteSpace: "nowrap", backgroundColor: p.content_status === "Zalistované na webe" ? "#dcfce7" : "#f3f4f6", color: p.content_status === "Zalistované na webe" ? "#166534" : "#475569", padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "bold", border: "1px solid #cbd5e1" }}>
                      {p.content_status}
                    </span>
                  ) : (
                    <span style={{ color: "#94a3b8", fontSize: "11px", fontStyle: "italic" }}>Spracúva sa</span>
                  )}
                </td>
                <td style={{ ...tdStyle, fontWeight: "bold", color: "#b45309", backgroundColor: "#fffbeb" }}>
                  {p.internal_sku ? p.internal_sku : <span style={{ color: "#94a3b8", fontWeight: "normal", fontSize: "12px" }}>Čaká na priradenie</span>}
                </td>

                {/* DYNAMICKÉ HODNOTY */}
                {selectedColumns.map(colKey => (
                  <td key={colKey} style={tdStyle}>
                    {colKey === "created_at" ? (
                      new Date(p.created_at).toLocaleDateString("sk-SK")
                    ) : colKey === "price" ? (
                      <strong style={{color: "#059669"}}>{p[colKey]} €</strong>
                    ) : (
                      <div style={colKey === "short_description" || colKey === "description" ? multiEllipsisStyle : ellipsisStyle} title={p[colKey]}>
                        {p[colKey] || "-"}
                      </div>
                    )}
                  </td>
                ))}

              </tr>
            ))}
          </tbody>
        </table>

        {/* 👇 STRÁNKOVANIE 👇 */}
        {!loading && sortedProducts.length > 0 && (
          <div style={paginationContainerStyle}>
            <div style={{ fontSize: "13px", color: "#64748b" }}>
              Zobrazených {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, sortedProducts.length)} z <strong>{sortedProducts.length}</strong> produktov
            </div>
            <div style={{ display: "flex", gap: "5px" }}>
              <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1 || itemsPerPage === "all"} style={pageBtnStyle(currentPage === 1 || itemsPerPage === "all")}>Predchádzajúca</button>
              {itemsPerPage !== "all" && Array.from({ length: totalPages }, (_, i) => i + 1).map(number => {
                if (number === 1 || number === totalPages || (number >= currentPage - 2 && number <= currentPage + 2)) {
                  return <button key={number} onClick={() => paginate(number)} style={currentPage === number ? activePageBtnStyle : pageBtnStyle(false)}>{number}</button>;
                } else if (number === currentPage - 3 || number === currentPage + 3) return <span key={number} style={{ padding: "5px 10px", color: "#94a3b8" }}>...</span>;
                return null;
              })}
              <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages || itemsPerPage === "all"} style={pageBtnStyle(currentPage === totalPages || itemsPerPage === "all")}>Ďalšia</button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "13px", color: "#475569" }}>
              <span>Zobraziť po:</span>
              <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(e.target.value === "all" ? "all" : Number(e.target.value)); setCurrentPage(1); }} style={pageSelectStyle}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value="all">Všetky</option>
              </select>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// 🔥 CSS-IN-JS STYLY
const thStyle = { padding: "12px 10px", color: "#334155", verticalAlign: "top", borderLeft: "1px solid #e2e8f0" };
const sortableHeaderContent = { display: "flex", alignItems: "center", gap: "5px", cursor: "pointer", fontWeight: "bold", userSelect: "none" };
const tdStyle = { padding: "12px 10px", verticalAlign: "middle", borderLeft: "1px solid #e2e8f0" };
const filterInputStyle = { display: "block", width: "100%", padding: "6px 8px", fontSize: "11px", marginTop: "8px", borderRadius: "6px", border: "1px solid #cbd5e1", outline: "none", boxSizing: "border-box", backgroundColor: "white", fontWeight: "normal" };
const ellipsisStyle = { maxWidth: "150px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#475569" };
const multiEllipsisStyle = { maxWidth: "300px", display: "-webkit-box", webkitLineClamp: "3", webkitBoxOrient: "vertical", overflow: "hidden", color: "#475569" };

const badgeSuccess = { background: "#dcfce7", color: "#166534", padding: "4px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "bold" };
const badgeDanger = { background: "#fee2e2", color: "#991b1b", padding: "4px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "bold" };
const badgeWarning = { background: "#fef3c7", color: "#92400e", padding: "4px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "bold" };

// STYLY PRE MENU STĹPCOV
const columnChooserPopupStyle = { position: "absolute", top: "100%", right: "0", marginTop: "12px", background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "16px", padding: "20px", boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)", zIndex: 1000, width: "300px" };
const popupHeaderStyle = { fontWeight: "800", fontSize: "15px", color: "#1e293b", marginBottom: "15px", paddingBottom: "10px", borderBottom: "1px solid #e2e8f0", textAlign: "left" };
const popupContentStyle = { maxHeight: "350px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "4px" };
const popupItemStyle = { display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "10px", padding: "6px 8px", fontSize: "13px", color: "#334155", cursor: "pointer", borderRadius: "8px", userSelect: "none", margin: "0", textAlign: "left", transition: "background-color 0.2s" };
const popupItemDisabledStyle = { ...popupItemStyle, color: "#94a3b8", cursor: "not-allowed", backgroundColor: "#f8fafc" };

// STYLY PRE STRÁNKOVANIE
const paginationContainerStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "15px 20px", borderTop: "1px solid #e2e8f0", backgroundColor: "#f8fafc", flexWrap: "wrap", gap: "15px" };
const pageBtnStyle = (disabled) => ({ padding: "6px 12px", border: "1px solid #cbd5e1", backgroundColor: disabled ? "#f1f5f9" : "white", color: disabled ? "#94a3b8" : "#334155", borderRadius: "6px", cursor: disabled ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: "600", transition: "all 0.2s" });
const activePageBtnStyle = { ...pageBtnStyle(false), backgroundColor: "#0f172a", color: "white", borderColor: "#0f172a" };
const pageSelectStyle = { padding: "6px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", outline: "none", cursor: "pointer", backgroundColor: "white" };